import React, { Component } from "react";
import moment from "moment";
import { getMpesaTransactions, getMpesaSummary } from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { exportToExcel } from "../../utils/exportToExcel";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import PeriodFilter, { allTime, periodBounds, periodToParams, periodLabel } from "../../components/Common/PeriodFilter";
import {
  C,
  Panel,
  Table,
  Chip,
  Btn,
  SearchPill,
  TabPills,
  MiniStat,
  StatRow,
  MeterList,
  PanelLink, Pager} from "../../components/Console/ConsoleUI";
import {
  isBranchScopedAdmin,
  getUserBranchName,
  hasModuleAccess,
} from "../../services/PermissionService";

/**
 * Payments ledger — screen E5 of the console redesign.
 *
 * Four figures across the top, the ledger itself beside a rail that says what
 * the money was paid with and what in it still needs a human. Same two
 * requests the previous screen made (/api/payments/transactions and
 * /api/payments/summary). Both get the shared PeriodFilter's params, so the
 * figures across the top cover the same dates as the ledger below; the
 * ledger also keeps receiving the from/to it accepted before.
 *
 * Branch scope is the server's, not ours: listTransactions pins a branch admin
 * to their own branch and gives a global admin the switcher's selection. The
 * Branch column stays on the table so a global admin can always see which one
 * a payment belongs to.
 *
 * The handoff's refund dialog is deliberately not built — see the note above
 * renderTable().
 */

const METHOD_LABELS = {
  mpesa: "M-PESA",
  loyalty: "Points only",
};

/**
 * What the payment was for. The list endpoint returns the linked ids
 * unpopulated, but which one is set is enough to name the service — no extra
 * request, and nothing inferred that the record does not state.
 */
const sourceOf = (p) => {
  if (p.orderFoodId || p.orderId) return { icon: "restaurant", label: "Food order" };
  if (p.roomBookingId || p.RoomId) return { icon: "bed", label: "Room booking" };
  if (p.tableBookingId) return { icon: "event_seat", label: "Table booking" };
  if (p.transportRequestId || p.TaxiId) return { icon: "local_taxi", label: "Transport" };
  return { icon: "receipt_long", label: "Other" };
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

class MpesaTransactions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      limit: 25,
      isLoaded: false,
      total: 0,
      items: [],
      summary: null,
      period: allTime(),
      filters: {
        method: "",
        search: "",
      },
    };
    this.searchTimer = null;
  }

  componentDidMount() {
    this.loadSummary();
    this.loadTransactions();
  }

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  loadSummary = () => {
    getMpesaSummary(periodToParams(this.state.period))
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          this.setState({ summary: result.data });
        }
      })
      .catch(() => {});
  };

  goToPage = (page) => this.loadTransactions(page, this.state.limit);
  changeLimit = (limit) => this.loadTransactions(1, limit);

  loadTransactions = (page = this.state.page, limit = this.state.limit) => {
    const { method, search } = this.state.filters;
    const { period } = this.state;
    // Was hard-coded to page 1, limit 100: the 101st transaction was simply
    // unreachable, and the footer said "Showing N of M" without a way forward.
    const params = { page, limit, ...periodToParams(period) };
    if (method) params.method = method;
    if (search) params.search = search;
    const bounds = periodBounds(period);
    if (bounds) {
      params.from = bounds.startDate;
      params.to = bounds.endDate;
    }

    this.setState({ isLoaded: false });
    getMpesaTransactions(params)
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const items = result.data.items || [];
          this.setState({
            isLoaded: true,
            items,
            total: result.data.total || items.length,
            page,
            limit,
          });
        } else {
          this.setState({ isLoaded: true });
          toast.error((result && result.message) || "Failed to load transactions");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading transactions");
      });
  };

  handleFilterChange = (field, value) => {
    this.setState({ filters: { ...this.state.filters, [field]: value } });
  };

  /** Typing narrows the ledger once you pause, rather than on a second click. */
  handleSearch = (value) => {
    this.setState({ filters: { ...this.state.filters, search: value } });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(this.loadTransactions, 400);
  };

  /** A new period reloads the figures too — they cover the same dates. */
  handlePeriod = (period) => {
    this.setState({ period }, () => {
      this.loadSummary();
      this.loadTransactions(1, this.state.limit);
    });
  };

  handleMethod = (method) => {
    this.setState({ filters: { ...this.state.filters, method } }, this.loadTransactions);
  };

  /**
   * Writes out the rows currently loaded. No new request and no figure that is
   * not already on the table.
   */
  handleExport = () => {
    const rows = this.state.items.map((p) => {
      const u = p.userId || {};
      return {
        Date: p.createdAt ? moment(p.createdAt).format("YYYY-MM-DD HH:mm") : "",
        Reference: p.transactionId || p.transactionReference || "",
        Guest: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
        Phone: p.customerMSISDN || "",
        Source: sourceOf(p).label,
        Branch: (p.branchId && p.branchId.name) || "",
        Method: METHOD_LABELS[p.payment_method] || p.payment_method || "",
        Gross: p.grossAmount != null ? p.grossAmount : "",
        Discount: p.discountAmount != null ? p.discountAmount : "",
        Charged: p.amount != null ? p.amount : "",
        Points: p.pointsRedeemed || 0,
        Status: p.status || "",
        Test: p.isMock ? "yes" : "no",
      };
    });
    if (rows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    exportToExcel(
      [{ name: "Ledger", data: rows }],
      `Payments_ledger_${moment().format("YYYY-MM-DD")}`
    );
  };

  // --- presentation ---------------------------------------------------------

  /** Method share, from the summary the KPI row already read (same period). */
  methodSplit() {
    const s = this.state.summary;
    if (!s || !s.totalCount) return [];
    const pct = (n) => (n / s.totalCount) * 100;
    return [
      { label: "M-PESA", value: `${Math.round(pct(s.mpesaCount || 0))}%`, pct: pct(s.mpesaCount || 0) },
      { label: "Points only", value: `${Math.round(pct(s.loyaltyCount || 0))}%`, pct: pct(s.loyaltyCount || 0) },
    ];
  }

  /**
   * The handoff's "Needs attention" rail. Its examples (delayed payouts,
   * refunds awaiting approval, an unreconciled drawer) have no counterpart in
   * this system, so the panel counts the three things in the loaded ledger a
   * finance admin actually has to chase.
   */
  attention() {
    const items = this.state.items || [];
    const mock = items.filter((p) => p.isMock).length;
    const unsettled = items.filter(
      (p) => p.payment_method === "mpesa" && String(p.status || "").toLowerCase() !== "completed"
    ).length;
    const unassigned = items.filter((p) => !p.branchId).length;
    return [
      {
        key: "unsettled",
        tone: "#fbf0dd",
        title: "M-PESA not confirmed",
        detail: `${unsettled} charge${unsettled === 1 ? "" : "s"} without a completed status`,
        n: unsettled,
      },
      {
        key: "mock",
        tone: "#fbe9e9",
        title: "Test payments in the ledger",
        detail: `${mock} row${mock === 1 ? "" : "s"} recorded against the M-PESA mock`,
        n: mock,
      },
      {
        key: "branch",
        tone: "#f7f0e5",
        title: "No branch on the record",
        detail: `${unassigned} payment${unassigned === 1 ? "" : "s"} predating branch scoping`,
        n: unassigned,
      },
    ];
  }

  statusChip(p) {
    const raw = String(p.status || "").toLowerCase();
    if (!raw) return <Chip tone="neutral">UNKNOWN</Chip>;
    if (raw === "completed") return <Chip tone="good">SETTLED</Chip>;
    if (raw.indexOf("fail") === 0 || raw === "cancelled") return <Chip tone="bad">{raw.toUpperCase()}</Chip>;
    return <Chip tone="warn">{raw.toUpperCase()}</Chip>;
  }

  render() {
    const { isLoaded, items, total, summary, filters, period, page, limit } = this.state;
    const scope = isBranchScopedAdmin() ? getUserBranchName() : null;

    const columns = [
      {
        key: "ref",
        label: "Txn",
        width: 150,
        render: (p) => (
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.ink, whiteSpace: "nowrap" }}>
              {p.transactionId || p.transactionReference || "—"}
            </div>
            {p.isMock && (
              <div style={{ marginTop: 3, fontSize: 11, letterSpacing: ".5px", color: C.red }}>TEST</div>
            )}
          </div>
        ),
      },
      {
        key: "guest",
        label: "Guest",
        render: (p) => {
          const u = p.userId || {};
          const name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
          return (
            <div>
              <div style={{ fontSize: 14, color: C.ink }}>{name || "Unknown"}</div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>{p.customerMSISDN || "—"}</div>
            </div>
          );
        },
      },
      {
        key: "source",
        label: "Source",
        render: (p) => {
          const s = sourceOf(p);
          return (
            <span
              style={{
                display: "flex", alignItems: "center", gap: 8,
                color: C.slate, whiteSpace: "nowrap",
              }}
            >
              <span className="ms" style={{ fontSize: 18, color: C.taupeLight }}>{s.icon}</span>
              {s.label}
            </span>
          );
        },
      },
      // Branch scope is enforced server-side; naming it here is how a global
      // admin tells one branch's takings from another's.
      {
        key: "branch",
        label: "Branch",
        render: (p) => (
          <span style={{ color: p.branchId ? C.slate : C.taupeLight }}>
            {(p.branchId && p.branchId.name) || "—"}
          </span>
        ),
      },
      {
        key: "method",
        label: "Method",
        render: (p) => (
          <Chip tone={p.payment_method === "loyalty" ? "neutral" : "good"}>
            {METHOD_LABELS[p.payment_method] || p.payment_method || "—"}
          </Chip>
        ),
      },
      {
        key: "date",
        label: "Date",
        render: (p) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {p.createdAt ? moment(p.createdAt).format("D MMM, HH:mm") : "—"}
          </span>
        ),
      },
      {
        key: "amount",
        label: "Amount",
        align: "right",
        width: 150,
        render: (p) => {
          const points = Number(p.pointsRedeemed || 0);
          const discount = Number(p.discountAmount || 0);
          return (
            <div style={{ whiteSpace: "nowrap" }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>{money(p.amount)}</div>
              {(discount > 0 || points > 0) && (
                <div style={{ fontSize: 12, color: C.taupeLight }}>
                  {money(p.grossAmount)} · {points} pts
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        width: 120,
        render: (p) => this.statusChip(p),
      },
    ];

    return (
      <ConsolePage
        title="Payments ledger"
        subtitle={
          scope
            ? `Branch: ${scope}`
            : isLoaded
            ? `${total} transaction${total === 1 ? "" : "s"} in scope`
            : "Loading the ledger…"
        }
        actions={
          <React.Fragment>
            <SearchPill
              value={filters.search}
              onChange={this.handleSearch}
              placeholder="Txn ref or phone"
              width={220}
            />
            <Btn icon="download" onClick={this.handleExport}>
              Export ledger
            </Btn>
          </React.Fragment>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <PeriodFilter value={period} onChange={this.handlePeriod} />
          <StatRow>
            <MiniStat
              label="Collected"
              value={summary ? money(summary.totalCollected) : "—"}
              hint={summary ? `${summary.totalCount} transaction${summary.totalCount === 1 ? "" : "s"} all time` : ""}
            />
            <MiniStat
              label="Paid with points"
              value={summary ? money(summary.totalDiscount) : "—"}
              hint="Monetary value of redeemed points"
            />
            <MiniStat
              label="Points redeemed"
              value={summary ? Number(summary.totalPointsRedeemed || 0).toLocaleString() : "—"}
              hint="Across every payment method"
            />
            <MiniStat
              dark
              label="M-PESA charges"
              value={summary ? Number(summary.mpesaCount || 0).toLocaleString() : "—"}
              hint={summary ? `${summary.loyaltyCount || 0} points-only orders` : ""}
            />
          </StatRow>

          <div style={{ display: "grid", gridTemplateColumns: "1.9fr 1fr", gap: 20, alignItems: "start" }}>
            <Panel style={{ padding: 0 }}>
              <TabPills
                value={filters.method}
                onChange={this.handleMethod}
                tabs={[
                  { value: "", label: "All methods" },
                  { value: "mpesa", label: "M-PESA" },
                  { value: "loyalty", label: "Points only" },
                ]}
                right={
                  <span style={{ fontSize: 13, color: C.taupeLight }}>
                    {period.mode === "all" ? "All dates" : periodLabel(period)}
                  </span>
                }
              />

              <div style={{ padding: "6px 8px" }}>
                <Table
                  columns={columns}
                  rows={isLoaded ? items : []}
                  empty={isLoaded ? "No payments match this filter." : "Loading payments…"}
                />
              </div>

              {isLoaded && items.length > 0 && (
                <div
                  style={{
                    padding: "4px 22px 12px", borderTop: `1px solid ${C.tint}`,
                  }}
                >
                  <Pager
                    page={page}
                    totalPages={Math.max(1, Math.ceil(total / limit))}
                    total={total}
                    limit={limit}
                    onPage={this.goToPage}
                    onLimit={this.changeLimit}
                    noun="transactions"
                  />
                </div>
              )}
            </Panel>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Panel title="Method split" subtitle={`${periodLabel(period)}, this branch scope`}>
                <MeterList items={this.methodSplit()} empty="No payments recorded yet" />
              </Panel>

              <Panel
                title="Needs attention"
                subtitle="In the rows loaded below"
                style={{ display: "flex", flexDirection: "column", minHeight: 260 }}
              >
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                  {this.attention().map((a) => (
                    <div
                      key={a.key}
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        background: a.n > 0 ? a.tone : C.surface,
                        opacity: a.n > 0 ? 1 : 0.7,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 500, color: C.brownDeep }}>{a.title}</div>
                      <div style={{ marginTop: 3, fontSize: 13, color: C.taupe }}>{a.detail}</div>
                    </div>
                  ))}
                </div>
                {hasModuleAccess("LOYALTY") && (
                  <PanelLink onClick={() => this.props.history.push("/loyalty/accounts")}>
                    Loyalty accounts
                  </PanelLink>
                )}
              </Panel>
            </div>
          </div>
        </div>
      </ConsolePage>
    );
  }
}

export default MpesaTransactions;
